/**
 * AssignedRatesTab - Unified view of all rate assignments across entity types
 *
 * Features:
 * - Lists all rate assignments (company/group/unit)
 * - Filter by entity type, rate profile, status, search
 * - Bulk operations: update dates, reassign rate, delete
 * - Click to edit individual assignments
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Layers,
  Truck,
  Calendar,
  Trash2,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RateAssignmentDrawer } from '@/components/settings/RateAssignmentDrawer';
import { useUnifiedRateAssignments } from '@/hooks/useUnifiedRateAssignments';
import { useEnergyRates } from '@/hooks/useEnergyRates';
import { formatRatePerKwh, formatEffectivePeriod } from '@/types/rates';
import type { UnifiedRateAssignment, EnergyRate } from '@/types/rates';
import { cn } from '@/lib/utils';

// Entity type config - minimal styling
const ENTITY_TYPE_CONFIG: Record<string, { icon: typeof Building2; label: string }> = {
  company: { icon: Building2, label: 'Company' },
  group: { icon: Layers, label: 'Group' },
  site: { icon: Layers, label: 'Site' },
  unit: { icon: Truck, label: 'Unit' },
};

// Status config - minimal styling
const STATUS_CONFIG: Record<string, { label: string }> = {
  active: { label: 'Active' },
  future: { label: 'Future' },
  expired: { label: 'Expired' },
};

interface AssignedRatesTabProps {
  rates: EnergyRate[];
  searchQuery?: string;
  entityTypeFilter?: string;
  rateFilter?: string;
  statusFilter?: string;
  refreshKey?: number;
  onRefresh?: () => void;
  onAssignmentCreated?: () => void;
}

export function AssignedRatesTab({
  rates,
  searchQuery = '',
  entityTypeFilter = '',
  rateFilter = '',
  statusFilter = '',
  refreshKey = 0,
  onRefresh,
  onAssignmentCreated: _onAssignmentCreated,
}: AssignedRatesTabProps) {
  // State
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingAssignment, setEditingAssignment] = useState<UnifiedRateAssignment | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageSize, setPageSize] = useState(50);

  // Bulk operation dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [datesDialogOpen, setDatesDialogOpen] = useState(false);
  const [newRateId, setNewRateId] = useState<string>('');
  const [newEffectiveFrom, setNewEffectiveFrom] = useState<string>('');
  const [newEffectiveTo, setNewEffectiveTo] = useState<string>('');

  // Hooks
  const {
    assignments,
    total,
    page,
    pageSize: _hookPageSize,
    hasMore,
    isLoading,
    isSubmitting,
    refresh,
    setPage,
    setFilters: setHookFilters,
    bulkUpdateDates,
    bulkReassign,
    bulkDelete,
  } = useUnifiedRateAssignments({ autoFetch: true, initialFilters: { pageSize } });

  const { refresh: refreshRates } = useEnergyRates();

  // Sync filters from props to hook (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Cast filter values to their proper union types
      const validEntityTypes = ['company', 'group', 'site', 'unit'] as const;
      const validStatuses = ['active', 'future', 'expired'] as const;

      const entityType = entityTypeFilter && validEntityTypes.includes(entityTypeFilter as typeof validEntityTypes[number])
        ? (entityTypeFilter as 'company' | 'group' | 'site' | 'unit')
        : undefined;

      const status = statusFilter && validStatuses.includes(statusFilter as typeof validStatuses[number])
        ? (statusFilter as 'active' | 'future' | 'expired')
        : undefined;

      setHookFilters({
        entityType,
        rateId: rateFilter || undefined,
        status,
        search: searchQuery || undefined,
        pageSize,
      });
      setSelectedIds(new Set()); // Clear selection on filter change
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, entityTypeFilter, rateFilter, statusFilter, pageSize, setHookFilters]);

  // Refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      refresh();
    }
  }, [refreshKey, refresh]);

  // Selection handlers
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === assignments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assignments.map((a) => a.id)));
    }
  }, [assignments, selectedIds]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Edit handler
  const handleEdit = useCallback((assignment: UnifiedRateAssignment) => {
    setEditingAssignment(assignment);
    setDrawerOpen(true);
  }, []);

  // Bulk operations
  const handleBulkDelete = useCallback(async () => {
    const success = await bulkDelete(Array.from(selectedIds));
    if (success) {
      setSelectedIds(new Set());
      setDeleteDialogOpen(false);
      onRefresh?.();
    }
  }, [bulkDelete, selectedIds, onRefresh]);

  const handleBulkReassign = useCallback(async () => {
    if (!newRateId) return;
    const success = await bulkReassign(Array.from(selectedIds), newRateId);
    if (success) {
      setSelectedIds(new Set());
      setReassignDialogOpen(false);
      setNewRateId('');
      onRefresh?.();
    }
  }, [bulkReassign, selectedIds, newRateId, onRefresh]);

  const handleBulkUpdateDates = useCallback(async () => {
    const success = await bulkUpdateDates(
      Array.from(selectedIds),
      newEffectiveFrom || undefined,
      newEffectiveTo || null
    );
    if (success) {
      setSelectedIds(new Set());
      setDatesDialogOpen(false);
      setNewEffectiveFrom('');
      setNewEffectiveTo('');
      onRefresh?.();
    }
  }, [bulkUpdateDates, selectedIds, newEffectiveFrom, newEffectiveTo, onRefresh]);

  // Drawer close handler
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setEditingAssignment(null);
    refresh();
    refreshRates();
    onRefresh?.();
  }, [refresh, refreshRates, onRefresh]);

  // Drawer submit handler (after successful update from drawer)
  const handleDrawerSubmit = useCallback(async () => {
    setDrawerOpen(false);
    setEditingAssignment(null);
    refresh();
    refreshRates();
    onRefresh?.();
  }, [refresh, refreshRates, onRefresh]);

  // Calculate page info
  const pageCount = Math.ceil(total / pageSize);
  const _startItem = (page - 1) * pageSize + 1;
  const _endItem = Math.min(page * pageSize, total);

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar (when items selected) */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border">
          <span className="text-sm font-medium">
            {selectedIds.size} selected
          </span>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDatesDialogOpen(true)}
            disabled={isSubmitting}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Update Dates
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReassignDialogOpen(true)}
            disabled={isSubmitting}
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Reassign Rate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isSubmitting}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      {/* Table - horizontal scroll on narrow viewports */}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 p-3">
                <Checkbox
                  checked={selectedIds.size === assignments.length && assignments.length > 0}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </th>
              <th className="text-left p-3 font-medium text-sm">Entity</th>
              <th className="text-left p-3 font-medium text-sm w-[100px]">Type</th>
              <th className="text-left p-3 font-medium text-sm">Rate</th>
              <th className="text-left p-3 font-medium text-sm w-[180px]">Period</th>
              <th className="text-center p-3 font-medium text-sm w-[90px]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && assignments.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading assignments...
                </td>
              </tr>
            ) : assignments.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  No rate assignments found
                </td>
              </tr>
            ) : (
              assignments.map((assignment) => {
                const entityConfig = ENTITY_TYPE_CONFIG[assignment.entityType] || ENTITY_TYPE_CONFIG.unit;
                const statusConfig = STATUS_CONFIG[assignment.status] || STATUS_CONFIG.active;
                const EntityIcon = entityConfig.icon;

                return (
                  <tr
                    key={assignment.id}
                    className="hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => handleEdit(assignment)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(assignment.id)}
                        onCheckedChange={() => toggleSelect(assignment.id)}
                        aria-label={`Select ${assignment.entityName}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <EntityIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{assignment.entityName}</div>
                          {assignment.parentCompanyName &&
                            assignment.entityType !== 'company' && (
                              <div className="text-xs text-muted-foreground truncate">
                                {assignment.parentCompanyName}
                              </div>
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-muted-foreground">
                        {entityConfig.label}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{assignment.rateName}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatRatePerKwh(assignment.gridImportRate)} import ·{' '}
                          {formatRatePerKwh(assignment.gridSellRate)} sell
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm truncate block">
                              {formatEffectivePeriod(assignment.effectiveFrom, assignment.effectiveTo)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            From: {assignment.effectiveFrom}
                            <br />
                            To: {assignment.effectiveTo || 'Ongoing'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="p-3 text-center">
                      <Badge
                        variant="outline"
                        className={cn(
                          'font-normal',
                          assignment.status === 'active' && 'border-[#8FB560]/50 bg-[#8FB560]/10 text-[#8FB560]',
                          assignment.status === 'future' && 'border-blue-500/50 bg-blue-500/10 text-blue-400',
                          assignment.status === 'expired' && 'border-border bg-muted/50 text-muted-foreground'
                        )}
                      >
                        {statusConfig.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer - page size on left, count in center, navigation on right */}
      <div className="flex items-center justify-between gap-3 px-2">
        {/* Left: Rows per page */}
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span className="whitespace-nowrap hidden sm:inline">Rows per page</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-[70px] bg-card border-border">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={size.toString()} className="focus:bg-muted">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Center: Total count */}
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} assignment{total !== 1 ? 's' : ''}
        </span>

        {/* Right: Navigation */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setPage(1)}
            disabled={page === 1 || isLoading}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setPage(page - 1)}
            disabled={page === 1 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[4rem] text-center whitespace-nowrap">
            {page} / {Math.max(1, pageCount)}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setPage(page + 1)}
            disabled={page === pageCount || !hasMore || isLoading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setPage(pageCount)}
            disabled={page === pageCount || isLoading}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Edit Drawer */}
      {editingAssignment && (
        <RateAssignmentDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          onSubmit={handleDrawerSubmit}
          assignment={{
            id: editingAssignment.id,
            rateId: editingAssignment.rateId,
            entityType: editingAssignment.entityType === 'site' ? 'group' : editingAssignment.entityType,
            entityId: editingAssignment.entityId,
            effectiveFrom: editingAssignment.effectiveFrom,
            effectiveTo: editingAssignment.effectiveTo,
            isOverride: editingAssignment.isOverride,
            priority: editingAssignment.priority,
            createdAt: editingAssignment.createdAt,
            createdBy: editingAssignment.createdBy,
            updatedAt: editingAssignment.createdAt,
          }}
          entityType={editingAssignment.entityType === 'site' ? 'group' : editingAssignment.entityType}
          entityId={editingAssignment.entityId}
          entityName={editingAssignment.entityName}
          rates={rates}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rate Assignments</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} rate assignment
              {selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={isSubmitting}
            >
              Delete {selectedIds.size} Assignment{selectedIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign to Different Rate</DialogTitle>
            <DialogDescription>
              Select a new rate profile for {selectedIds.size} assignment
              {selectedIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newRateId} onValueChange={setNewRateId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a rate profile" />
              </SelectTrigger>
              <SelectContent>
                {rates.map((rate) => (
                  <SelectItem key={rate.id} value={rate.id}>
                    <div className="flex flex-col">
                      <span>{rate.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatRatePerKwh(rate.gridImportRate)} import
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkReassign}
              disabled={isSubmitting || !newRateId}
              className="bg-[#8FB560] hover:bg-[#7a9d4e]"
            >
              Reassign {selectedIds.size} Assignment{selectedIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Dates Dialog */}
      <Dialog open={datesDialogOpen} onOpenChange={setDatesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Effective Dates</DialogTitle>
            <DialogDescription>
              Update the effective dates for {selectedIds.size} assignment
              {selectedIds.size !== 1 ? 's' : ''}. Leave fields empty to keep current values.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Effective From</label>
              <Input
                type="date"
                value={newEffectiveFrom}
                onChange={(e) => setNewEffectiveFrom(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Effective To (optional)</label>
              <Input
                type="date"
                value={newEffectiveTo}
                onChange={(e) => setNewEffectiveTo(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for ongoing assignment
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDatesDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpdateDates}
              disabled={isSubmitting || (!newEffectiveFrom && !newEffectiveTo)}
              className="bg-[#8FB560] hover:bg-[#7a9d4e]"
            >
              Update {selectedIds.size} Assignment{selectedIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
